import { Stack, Typography } from "@mui/material";
import { groupBy } from "lodash";
import { useTranslation } from "react-i18next";
import { To, useParams } from "react-router";

import { MapPanel, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import { VisitListItem } from "~/modules/plannerV2/components/VisitListItem";
import { URGENCY_ORDERING } from "~/modules/plannerV2/util/calculateStatusBuckerFromAreaStatus";

import { PlannerV2AreaDetailPanelHeader } from "../PlannerV2AreaDetailPanelHeader";
import { PlannerV2AreaDetailPanelFragment } from "./fragments.generated";
import { PlannerV2AreaDetailSkeleton } from "./skeleton";

type PlannerV2AreaDetailProps = {
    area?: PlannerV2AreaDetailPanelFragment | null;
    backHref: To;
    visitOpenHref: (visitId: string) => To;
    selectedDate: Date;
};

export function PlannerV2AreaDetail({ area, backHref, visitOpenHref, selectedDate }: PlannerV2AreaDetailProps) {
    const { t } = useTranslation("plannerV2");
    const { date } = useParams();

    if (!date) throw new Error("We need a date");

    if (!area) {
        return <PlannerV2AreaDetailSkeleton backHref={backHref} />;
    }

    const groupedVisits = groupBy(area.plannerVisits, "plannerStatus");
    const sortedEntries = URGENCY_ORDERING.map(status => [status, groupedVisits[status]] as const).filter(
        ([_, visits]) => visits && visits.length > 0,
    );

    return (
        <MapPanel>
            <PlannerV2AreaDetailPanelHeader backHref={backHref} area={area} />

            <MapPanelScrollableArea>
                {sortedEntries.map(([status, visits]) => (
                    <Stack key={status}>
                        <Typography
                            variant="overline"
                            sx={theme => ({
                                position: "sticky",
                                top: 0,
                                backgroundColor: theme.palette.background.paper,
                                paddingInline: 2,
                                paddingBlock: 1,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            })}
                        >
                            {t(`plannerStatus.${status}`)}
                        </Typography>
                        {visits.map(visit => (
                            <VisitListItem
                                selectedDate={selectedDate}
                                key={visit.id}
                                href={visitOpenHref(visit.id)}
                                visit={visit}
                            />
                        ))}
                    </Stack>
                ))}
            </MapPanelScrollableArea>
        </MapPanel>
    );
}
